import { defineStore } from "pinia";
import {
  collection,
  query,
  getFirestore,
  getDocs,
  orderBy,
  // Escrituras vía cloud functions (no Firestore directo)
} from "firebase/firestore";
import { userUserStore } from "../stores/user";
import { agregarSector } from "./cloudFunctions/agregarSector";
import { eliminarSector } from "./cloudFunctions/eliminarSector";

export interface Sector {
  id: string;
  nombre: string;
  orden: number;
  activo?: boolean;
}

export const useSectoresStore = defineStore({
  id: "sectores",
  state: () => ({
    sectores: [] as Sector[],
    cargando: false
  }),
  actions: {
    async getFromFirestore() {
      const db = getFirestore();
      const userStore = userUserStore();
      const empresaId = userStore.empresa?.id;
      if (!empresaId) return;

      this.cargando = true;
      try {
        const collectionRoute = `empresas/${empresaId}/sectores`;
        const q = query(
          collection(db, collectionRoute),
          orderBy("orden")
        );
        const snapshot = await getDocs(q);
        this.sectores = snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            nombre: data.nombre ?? "",
            orden: data.orden ?? 0,
            activo: data.activo === true
          };
        }).filter(s => s.activo === true);
      } finally {
        this.cargando = false;
      }
    },
    async crear(nombre: string, orden?: number): Promise<boolean> {
      const userStore = userUserStore();
      const empresaId = userStore.empresa?.id;
      if (!empresaId) return false;

      const nombreTrim = nombre.trim();
      const ordenNum =
        typeof orden === "number"
          ? orden
          : this.sectores.reduce((max, s) => Math.max(max, s.orden ?? 0), 0) + 1;

      try {
        const cantidadAntes = this.sectores.length;
        const result = await agregarSector({
          nombre: nombreTrim,
          orden: ordenNum
        });

        if (result?.exito) {
          // Refrescamos para sincronizar ID/orden desde backend.
          await this.getFromFirestore();
          const existeCreado = this.sectores.some(
            s => (s.nombre ?? "").trim().toLowerCase() === nombreTrim.toLowerCase()
          );
          const aumentoCantidad = this.sectores.length > cantidadAntes;
          return existeCreado || aumentoCantidad;
        }

        console.error("Error al crear sector (cloud function):", result?.errorMsg);
        return false;
      } catch (error) {
        console.error("Error al crear sector:", error);
        return false;
      }
    },
    async eliminar(sectorId: string): Promise<boolean> {
      const userStore = userUserStore();
      const empresaId = userStore.empresa?.id;
      if (!empresaId) return false;

      try {
        const result = await eliminarSector(sectorId);

        if (result?.exito) {
          // Refrescamos para sincronizar mesas/sectores re-asignados desde backend.
          await this.getFromFirestore();
          return true;
        }

        return false;
      } catch (error) {
        console.error("Error al eliminar sector:", error);
        return false;
      }
    }
  }
});
